import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { processTableID, showLoader, toggleAlertMessage, updateProgressBar, getStepFunctionStatus } from '../action.js';
import { Actions as ADMIN_ACTIONS } from './adminActions';
import createSignedRequest from './createSignedRequest';
import { Actions as FACILITY_ACTIONS } from './facilityActions';
import { checkReportingYearEndIssue, getOrganisation } from './organisationActions';
import WorkerFactory from '../../workers/worker.js';
import filterWorker from '../../workers/filter.worker.js';
import { updateDownloadStatus } from './reportActions.js';
const { apiURL, getAppVersion } = require('./apiURL');
const { versionToPostfix } = require('../../components/utils/GlobalFunctions');

const Types = {
  USAGE_DELETE: 'USAGE_DELETE',
  USAGE_UPDATE: 'USAGE_UPDATE',
  USAGE_UPDATE_TOTALS: 'USAGE_UPDATE_TOTALS',
  USAGE_TOGGLE_STATUS: 'USAGE_TOGGLE_STATUS',

  USAGE_EDIT_MODAL: 'USAGE_EDIT_MODAL',
  USAGE_SHOW_DATA_MODAL: 'USAGE_SHOW_DATA_MODAL',
  USAGE_SHOW_DATA_DELETE_MODAL: 'USAGE_SHOW_DATA_DELETE_MODAL',

  USAGE_UPLOAD_TYPE: 'USAGE_UPLOAD_TYPE',
  USAGE_GET_TEMPALATES: 'USAGE_GET_TEMPLATES',
  USAGE_IMPORT_LINK: 'USAGE_IMPORT_LINK',
  USAGE_IMPORT_SHOW_MODAL: 'USAGE_IMPORT_SHOW_MODAL',
  USAGE_IMPORT_SHOW_HISTORY: 'USAGE_IMPORT_SHOW_HISTORY',
  USAGE_MY_UPLOADS: 'USAGE_MY_UPLOADS',
  USAGE_OPTIONS: 'USAGE_OPTIONS',
  USAGE_CACHE_OPTIONS: 'USAGE_CACHE_OPTIONS',
  USAGE_IMPORT_ANALYSIS: 'USAGE_IMPORT_ANALYSIS',
  LIST_USAGE_DETAILS: 'USAGE_LIST_DETAILS',
  USAGE_PREVIOUS_DATES: 'USAGE_PREVIOUS_DATES',
  USAGE_RESET_MODAL: 'USAGE_RESET_MODAL',
};

const updateUsageData = (payload) => ({
  type: Types.USAGE_UPDATE,
  payload,
});

const setPreviousDates = (newUsage) => ({ type: Types.USAGE_PREVIOUS_DATES, newUsage });

export const showAnalysisImportModal = (status, payload) => ({
  type: Types.USAGE_IMPORT_ANALYSIS,
  status,
  payload,
});

const saveUsageData = (usage, adminPortal) => {
  const usageData = usage?.map((u) => ({
    id: u?.id || null,
    facility: u?.facility?.value || u?.facility,
    entity: u?.entity?.value || u?.entity,
    dateStart: u?.dateStart,
    dateEnd: u?.dateEnd,
    userLabel: u?.userLabel || null,
    subentity: u?.subentity?.value || u?.subentity,
    metric: u?.metric?.value || u?.metric,
    production: u?.production?.value || u?.production || null,
    fuel: u?.fuel?.value || u?.fuel || null,
    load: u?.load?.value || u?.load || null,
    seat: u?.seat?.value || u?.seat || null,
    originlocation: u?.originlocation?.value || u?.originlocation || null,
    finallocation: u?.finallocation?.value || u?.finallocation || null,
    factorprovider: u?.factorprovider?.value || u?.factorprovider || null,
    price: u?.price?.value || u?.price || null,
    organisation: u?.organisation?.value || u?.organisation,
    vehicle: u?.vehicle?.value || u?.vehicle || null,
    waste: u?.waste?.value || u?.waste || null,
    usage: parseFloat(u?.usage),
    waterTreatment: u?.waterTreatment || null,
    solarConsumed: u?.solarConsumed || null,
    fuel_mix: u?.fuel_mix ? JSON.stringify(u?.fuel_mix) : null,
    currency: u?.currency || null,
    isSpend: u?.isSpend || null,
    suppliersEmissionsFactor: u?.suppliersEmissionsFactor || null,
    createdBy: u?.createdBy || null,
    createdAt: u?.createdAt || null,
    usage_derivation: u?.usage_derivation ?? null,
    ai_assisted: u?.ai_assisted ?? null,
  }));

  const usageDataLength = usageData.length;
  let chunked_usage;

  if (usageDataLength > 200) {
    chunked_usage = _.chunk(usageData, 200);
  } else if (adminPortal) {
    //chunk by organisation.
    chunked_usage = usageData.reduce((acc, item) => {
      const index = acc.findIndex((chunk) => chunk[0].organisation === item.organisation);

      if (index !== -1) {
        acc[index].push(item);
      } else {
        acc.push([item]);
      }

      return acc;
    }, []);
  } else {
    chunked_usage = [];
    chunked_usage.push(usageData);
  }

  return async (dispatch, getState) => {
    const state = getState();

    let response;

    dispatch(updateProgressBar(0));

    try {
      const tableIDs = [];

      for (const usageArr of chunked_usage) {
        const tableID = uuidv4();
        tableIDs.push(tableID);
        let orgDetails = state?.organisation?.details;
        if (adminPortal) orgDetails = _.find(state?.admin?.organisations, { id: usageArr[0].organisation });
        const { reportingYearEndMonth, reportingYearEndDay } = orgDetails;

        response = await axios(
          await createSignedRequest(
            'POST',
            apiURL + `/my/usage/${reportingYearEndDay}/${reportingYearEndMonth}/${tableID}`,
            JSON.stringify(usageArr),
            {
              Organisation: orgDetails.id,
            },
          ),
        );

        if (!response.data.success) {
          dispatch(updateProgressBar(-1));
          dispatch(toggleAlertMessage(true, response.data.message));
          dispatch(showUsageDataModal(false, null));
          return;
        }
      }

      const processedCounts = new Array(tableIDs.length).fill(0);
      const chunkedUsageLength = chunked_usage.length;

      const usageParamsObj = {
        processedCounts,
        chunkedDataLength: chunkedUsageLength,
        totalDataLength: usageDataLength,
        state,
        dispatch,
        type: 'usage',
      };

      await Promise.all(tableIDs.map(async (tableID, index) => await processTableID(tableID, { ...usageParamsObj, index })));

      if (!adminPortal && usageDataLength === 1) dispatch(setPreviousDates(usageData[0]));

      const newState = getState();

      if (adminPortal && newState.progress === 100) {
        const { filters, page, tableSize } = state?.admin?.openSearchTerms;
        setTimeout(async () => {
          dispatch(ADMIN_ACTIONS.getItemsByTermsFromIndex('usageData', filters, page, tableSize)); // this triggers the fetch of the new/edited CF from the CF admin page
        }, 1500);
      } else if (state?.profile?.details?.cyfAdmin && getState()?.usageData?.length > 0)
        dispatch(ADMIN_ACTIONS.initializeCheckBoxes());
    } catch (e) {
      console.log('saveUsageData Error:', e);
      dispatch(updateProgressBar(-1));
      dispatch(toggleAlertMessage(true, 'There was an error saving your usage data.', 'error', 5000));
    }
  };
};

const dispatchDeleteUsageData = (payload) => ({
  type: Types.USAGE_DELETE,
  payload,
});

function filterProperties(dataArray, properties) {
  return dataArray.map((item) => {
    let newItem = {};
    properties.forEach((prop) => {
      if (item.hasOwnProperty(prop)) {
        newItem[prop] = item[prop];
      }
    });
    return newItem;
  });
}

const deleteUsageData = (items, adminPortal) => {
  const usageDataLength = items.length;
  let chunked_usage;

  items = filterProperties(items, ['source', 'organisation', 'invoiceLineItemId', 'id', 'isParent']);

  if (usageDataLength > 100) {
    chunked_usage = _.chunk(items, 100);
  } else {
    chunked_usage = [];
    chunked_usage.push(items);
  }

  const numOfChunks = chunked_usage.length;

  return async (dispatch, getState) => {
    const state = getState();

    let response;
    let counter = 0;

    dispatch(updateProgressBar(0));
    let deletedItems = [];
    try {
      for (const usageArr of chunked_usage) {
        response = await axios(
          await createSignedRequest('DELETE', apiURL + `/my/usage`, JSON.stringify(usageArr), {
            Organisation: String(state.currentOrganisation),
          }),
        );

        counter++;
        const perecentage = (counter / numOfChunks) * 100;
        const tempArray = response?.data?.deletedItems;
        if (tempArray?.length > 0) {
          deletedItems = deletedItems.concat(tempArray);
        }
        dispatch(updateProgressBar(perecentage));
      }

      console.log('deletedUsageData', response);
      if (!adminPortal) dispatch(dispatchDeleteUsageData(deletedItems));

      if (response.data.error || (!response.data.success && items.length >= 2)) {
        dispatch(toggleAlertMessage(true, response.data.message, 'error'));
      } else dispatch(toggleAlertMessage(true, response.data.message, 'success'));

      if (!adminPortal) dispatch(updateUsageTotals(null, true));
      dispatch(showUsageDataDeleteModal(false, null));

      if (adminPortal) {
        const { filters, page, tableSize } = state?.admin?.openSearchTerms;
        setTimeout(async () => {
          dispatch(ADMIN_ACTIONS.getItemsByTermsFromIndex('usageData', filters, page, tableSize)); // this triggers the fetch of the new/edited CF from the CF admin page
        }, 1500);
      } else if (state?.profile?.details?.cyfAdmin && getState()?.usageData?.length > 0)
        dispatch(ADMIN_ACTIONS.initializeCheckBoxes());

      dispatch(checkReportingYearEndIssue(state.currentOrganisation));
    } catch (e) {
      console.log('deletedUsageData Error:', e);
    }

    setTimeout(function () {
      dispatch(ADMIN_ACTIONS.toggleModal(false, null));
    }, 2000);
  };
};

const listUsageDetails = (response) => ({
  type: Types.LIST_USAGE_DETAILS,
  payload: response,
});

const getUsageById = (id) => {
  return async (dispatch, getState) => {
    dispatch(showLoader(true));
    try {
      const response = await axios(await createSignedRequest('GET', apiURL + `/my/usage?usageId=${id}`, null, null));
      console.log('getUsageById', response);
      dispatch(listUsageDetails(response.data));
    } catch (e) {
      console.log('getUsageById Error:', e);
    }
    dispatch(showLoader(false));
  };
};

const updateUsageTotals = (response, flag, year = null) => ({
  type: Types.USAGE_UPDATE_TOTALS,
  response,
  flag,
  year,
});

const toggleProcessingUsage = (saving) => ({
  type: Types.USAGE_TOGGLE_STATUS,
  saving,
});

const getUsageTotals = (year) => {
  return async (dispatch, getState) => {
    const state = getState();

    const currentOrganisation = state.currentOrganisation;

    const { reportingYearEndMonth, reportingYearEndDay } = state?.organisation?.details || {};

    if (!reportingYearEndMonth || !reportingYearEndDay) {
      dispatch(updateUsageTotals([], false));
      return;
    }

    dispatch(getOrganisation());
    dispatch(FACILITY_ACTIONS.getFacilities());

    dispatch(toggleProcessingUsage(true));
    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          apiURL + `/my/usage/${reportingYearEndDay}/${reportingYearEndMonth}/totals?year=${year}`,
          null,
          {
            Organisation: String(currentOrganisation),
          },
        ),
      );

      const { url, error } = response?.data;

      if (error) {
        dispatch(toggleAlertMessage(true, error, 'error'));
        dispatch(toggleProcessingUsage(false));
        return;
      }

      const usageData = await fetch(url).then((res) => res.json());

      dispatch(updateUsageTotals(usageData, false, response?.data?.year));
      if (state?.profile?.details?.cyfAdmin && getState()?.usageData?.length > 0) {
        dispatch(ADMIN_ACTIONS.initializeCheckBoxes());
      }

      dispatch(checkReportingYearEndIssue(currentOrganisation));
    } catch (e) {
      console.log('getUsage Totals Error:', e);
    }
  };
};

const showUsageDataDeleteModal = (status, item) => ({
  type: Types.USAGE_SHOW_DATA_DELETE_MODAL,
  status,
  item,
});

export const editUsageModal = (selectedInput, event) => ({
  type: Types.USAGE_EDIT_MODAL,
  selectedInput,
  event,
});

const showUsageDataModal = (status, item, adminPortal) => ({
  type: Types.USAGE_SHOW_DATA_MODAL,
  status,
  item,
  adminPortal,
});

const uploadUsageType = (response) => ({
  type: Types.USAGE_UPLOAD_TYPE,
  payload: response,
});

const setTemplateOptions = (response) => ({
  type: Types.USAGE_GET_TEMPALATES,
  payload: response,
});

const usageImportUploadLink = (response) => ({
  type: Types.USAGE_IMPORT_LINK,
  payload: response,
});

const getUploadLink = (file, action) => {
  return async (dispatch, getState) => {
    const state = getState();
    const uploadObj = {
      name: file.name,
      type: file.type,
    };

    if (action === 'deleteOrgs') {
      uploadObj.action = 'deleteOrgs';
    } else if (action === 'importMixedTemplates') {
      uploadObj.action = 'importMixedTemplates';
    }

    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/upload', JSON.stringify(uploadObj), {
          Organisation: String(state.currentOrganisation),
        }),
      );

      console.log('getUploadLink', response);
      dispatch(usageImportUploadLink(response.data));
    } catch (e) {
      console.log('getUploadLink Error:', e);
    }
  };
};

const loadMyUploads = (response) => ({
  type: Types.USAGE_MY_UPLOADS,
  payload: response,
});

const getMyUploads = () => {
  return async (dispatch, getState) => {
    const state = getState();

    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/uploads', null, { Organisation: String(state.currentOrganisation) }),
      );

      console.log('getMyUploads', response);
      dispatch(loadMyUploads(response.data));
    } catch (e) {
      console.log('getMyUploads Error:', e);
    }
  };
};

const confirmUploadEstimate = (id) => {
  return async (dispatch, getState) => {
    dispatch(showLoader(true));
    const state = getState();

    const upload = _.find(state.myUploads, { id });

    try {
      const tableID = uuidv4();
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/import',
          JSON.stringify({ uploadId: id, key: upload.jsonOutputKey, type: upload.type, estimate: false, tableID: tableID }),
          { Organisation: String(state.currentOrganisation) }
        )
      );

      console.log('confirmUploadEstimate', response);
      dispatch(toggleAlertMessage(true, 'We are currently preparing your Import...', 'success', 12000));
      const importUsageParamsObj = { state, dispatch, type: 'importUsage' };
      await processTableID(tableID, importUsageParamsObj);
      if (state?.profile?.details?.cyfAdmin && getState()?.usageData?.length > 0) {
        dispatch(ADMIN_ACTIONS.initializeCheckBoxes());
      }
    } catch (e) {
      console.log('confirmUploadEstimate Error:', e);
      dispatch(toggleAlertMessage(true, 'Your import failed!', 'error', 5000));
    }
  };
};

const processUpload = (file, action) => {
  return async (dispatch, getState) => {
    const state = getState();

    dispatch(showLoader(true));
    const importUploadKey = state.importUploadKey;
    const importUploadLink = state.importUploadLink;
    const usageUploadType = state.usageUploadType;

    dispatch(toggleAlertMessage(true, 'Your File Is Being Uploaded!', 'success', 2000));
    const tableID = uuidv4();

    const input = {
      key: importUploadKey,
      type: usageUploadType.value,
      tableID,
      estimate: true,
    };

    if (action === 'deleteOrgs') {
      input.action = action;
      input.uploadType = 'deleteOrgs';
    } else if (action === 'importMixedTemplates') {
      input.action = action;
      input.uploadType = 'importMixedTemplates';
    } else {
      // Import Usage Data
      input.uploadType = 'importUsage';
    }

    // step 1 upload to AWS
    fetch(importUploadLink, {
      credentials: 'include',
      method: 'PUT',
      body: file,
      headers: {
        Accept: 'application/json',
        'Content-Type': file.type,
      },
    })
      .then(async () => {
        try {
          const response = await axios(
            await createSignedRequest('POST', apiURL + '/my/import', JSON.stringify(input), {
              Organisation: String(state.currentOrganisation),
            }),
          );

          if (!response.data.success) {
            dispatch(toggleAlertMessage(true, response.data.message, 'error', 5000));
            dispatch(showLoader(false));
            return;
          }

          console.log('File Upload Succesful', response);
          dispatch(toggleAlertMessage(true, 'We are currently preparing your Import...', 'success', 12000));
          const importUsageParamsObj = { state, dispatch, type: input.uploadType };
          await processTableID(tableID, importUsageParamsObj);
          if (state?.profile?.details?.cyfAdmin && getState()?.usageData?.length > 0) {
            dispatch(ADMIN_ACTIONS.initializeCheckBoxes());
          }
        } catch (e) {
          console.log('File Upload Error:', e);
          dispatch(toggleAlertMessage(true, 'Your File upload has failed!', 'error', 5000));
          dispatch(showLoader(false));
        }
      })
      .catch((e) => {
        console.log('Usage Upload Template Upload Error:', e);
        dispatch(toggleAlertMessage(true, 'Your File upload has failed!', 'error', 5000));
        dispatch(showLoader(false));
      });
  };
};

const getFilteredFactors = (dEnd, subentity, gm, metric) => {
  return async (dispatch, getState) => {
    console.time('getFilteredFactors');
    const state = getState();
    const workerInstance = new WorkerFactory(filterWorker);
    // dispatch(showLoader(true));

    try {
      const gridSources = new Set();
      const formattedDEnd = moment(dEnd).format('YYYY-MM-DD');

      if (gm) {
        const sources = new Set(
          state.admin?.adminPortal?.electricitySources?.filter((source) => !source.hideInUI).map((s) => s.id) ?? [],
        );

        const marketFactorsPromise = new Promise((resolve) => {
          workerInstance.onmessage = (res) => {
            res.data?.forEach((factor) => {
              if (sources.has(factor.source) && !gridSources.has(factor.source)) {
                gridSources.add(factor.source);
              }
            });
            resolve();
          };
        });

        workerInstance.postMessage({ data: state.cache?.marketFactors, subentity, filterBy: {}, formattedDEnd });
        await marketFactorsPromise;
      }

      const conversionFactorsPromise = new Promise((resolve) => {
        workerInstance.onmessage = (res) => {
          dispatch(updateUsageOptions({ conversionFactors: res.data || [], gridSources: Array.from(gridSources) }));
          resolve();
        };
      });

      workerInstance.postMessage({ data: state.cache?.conversionFactors, subentity, filterBy: { metric }, formattedDEnd });
      await conversionFactorsPromise;

      workerInstance.terminate();
      // dispatch(showLoader(false));
    } catch (e) {
      console.error('updateUsageOptions Error:', e);
      // dispatch(showLoader(false));
    }
    console.timeEnd('getFilteredFactors');
  };
};

const subentityRowToSelectOption = (row) => ({
  value: row?.id,
  label: row?.name || row?.priceType,
  description: row?.description,
  metric: row?.metric || null,
  fuel_mix: row?.calc_market || null,
});

const getUsageModalOrganisationId = (state) => {
  const modal = state.usageDataModal;
  if (modal?.adminPortal && modal?.item?.organisation) {
    return String(modal.item.organisation);
  }
  return String(state.currentOrganisation);
};

/**
 * Calls POST /my/factor-suggest for natural-language usage matching.
 * @param {object} params
 * @param {string} params.text
 * @param {string} params.date_end - YYYY-MM-DD
 * @param {string} [params.org_country] - ISO 3166-1 alpha-2
 * @param {boolean} [params.include_average_fallback]
 * @param {Record<string, string>} [params.clarify_replies] - answers keyed by clarify item id (option value or free text)
 * @param {number} [params.clarify_round] - echo prior response clarify_round during multi-step clarify
 * @param {string} [params.originlocation] - catalogue UUID when origin location is chosen (modal or clarify)
 * @param {string} [params.finallocation] - catalogue UUID when final location is chosen
 * @param {string} [params.price] - catalogue UUID when price band is chosen
 * @param {string} [params.date_start] - optional YYYY-MM-DD for activity window
 * @param {string} [params.factorprovider_preferred]
 * @param {boolean} [params.factorprovider_strict]
 * @param {boolean} [params.add_ons_strict]
 * @returns {Promise<{ data?: object, quota?: string, error?: string, detail?: string }>}
 */
const suggestFactorFromText = (params = {}) => {
  const {
    text,
    date_end,
    date_start,
    org_country,
    include_average_fallback = true,
    clarify_replies,
    clarify_round,
    originlocation,
    finallocation,
    price,
    factorprovider_preferred,
    factorprovider_strict,
    add_ons_strict,
  } = params;
  return async (dispatch, getState) => {
    const state = getState();
    const organisationId = getUsageModalOrganisationId(state);
    const trimmed = String(text || '').trim();
    if (!trimmed) {
      dispatch(toggleAlertMessage(true, 'Enter a short description of your activity.', 'error'));
      return { error: 'EMPTY_TEXT' };
    }
    if (!date_end) {
      dispatch(toggleAlertMessage(true, 'Set an end date before requesting suggestions.', 'error'));
      return { error: 'MISSING_DATE' };
    }

    const od = state.organisation?.details || {};
    const body = {
      text: trimmed,
      date_end,
      include_average_fallback,
    };
    if (date_start) body.date_start = date_start;
    if (org_country) body.org_country = org_country;
    if (clarify_replies && typeof clarify_replies === 'object' && Object.keys(clarify_replies).length > 0) {
      body.clarify_replies = clarify_replies;
    }
    if (clarify_round !== undefined && clarify_round !== null && !Number.isNaN(Number(clarify_round))) {
      body.clarify_round = Number(clarify_round);
    }
    if (originlocation) body.originlocation = String(originlocation);
    if (finallocation) body.finallocation = String(finallocation);
    if (price) body.price = String(price);

    const fromOrg = _.pickBy(
      {
        factorprovider_preferred: od.factorprovider_preferred ?? od.factorproviderPreferred,
        factorprovider_strict: od.factorprovider_strict ?? od.factorproviderStrict,
        add_ons_strict: od.add_ons_strict ?? od.addOnsStrict,
      },
      (v) => v !== undefined && v !== null && v !== '',
    );
    Object.assign(body, fromOrg);
    if (factorprovider_preferred !== undefined) body.factorprovider_preferred = factorprovider_preferred;
    if (factorprovider_strict !== undefined) body.factorprovider_strict = factorprovider_strict;
    if (add_ons_strict !== undefined) body.add_ons_strict = add_ons_strict;

    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/factor-suggest', JSON.stringify(body), {
          Organisation: organisationId,
        }),
      );

      const headers = response.headers || {};
      const quota =
        headers['x-cyf-ai-quota'] ||
        headers['X-CYF-AI-Quota'] ||
        headers['X-Cyf-Ai-Quota'];
      if (quota === 'warn' || quota === 'soft-limit') {
        dispatch(
          toggleAlertMessage(true, 'AI suggestion usage is running high; results may be limited.', 'warning', 8000),
        );
      }

      return { data: response.data, quota };
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 429 && data?.code === 'AI_QUOTA_EXCEEDED') {
        dispatch(toggleAlertMessage(true, data?.detail || 'AI quota exceeded. Try again later.', 'error'));
        return { error: 'AI_QUOTA_EXCEEDED', detail: data?.detail };
      }
      const msg = data?.detail || data?.message || e?.message || 'Could not get suggestions.';
      dispatch(toggleAlertMessage(true, msg, 'error'));
      return { error: 'REQUEST_FAILED', detail: data?.detail };
    }
  };
};

/**
 * Stores org-scoped phrase → factor preference (FactorMatchCache). Call after user confirms a suggest match.
 * @param {object} params
 * @param {string} params.text - same wording as factor-suggest (and clarify flow)
 * @param {string} params.factor_id
 * @param {string} [params.factor_name]
 * @param {string} [params.org_country]
 * @param {Record<string, string>} [params.clarify_replies]
 * @returns {Promise<{ success?: boolean, cache_key?: string, error?: boolean }>}
 */
/**
 * Records the user's factor choice. Always writes an audit record; optionally
 * writes to FactorMatchCache so the same text returns cache_hit next time.
 * @param {object} params
 * @param {string} params.text - original free-text description
 * @param {string} params.factor_id
 * @param {string} [params.factor_name]
 * @param {string} [params.org_country]
 * @param {object} [params.clarify_replies]
 * @param {boolean} [params.remember_choice=true] - write to FactorMatchCache
 * @param {string[]} [params.alternates_shown] - factor_ids of alternates the user saw
 * @param {number} [params.confidence_score] - best.signals score the user acted on
 * @param {number} [params.clarify_rounds] - total clarify rounds completed
 */
const submitFactorSuggestFeedback = (params = {}) => {
  const {
    text, factor_id, factor_name, org_country, clarify_replies,
    remember_choice, alternates_shown, confidence_score, clarify_rounds,
  } = params;
  return async (dispatch, getState) => {
    const state = getState();
    const organisationId = getUsageModalOrganisationId(state);
    const trimmed = String(text || '').trim();
    if (!trimmed || !factor_id) {
      return { error: true };
    }
    const body = { text: trimmed, factor_id: String(factor_id) };
    if (factor_name) body.factor_name = factor_name;
    if (org_country) body.org_country = org_country;
    if (clarify_replies && typeof clarify_replies === 'object' && Object.keys(clarify_replies).length > 0) {
      body.clarify_replies = clarify_replies;
    }
    if (remember_choice === false) body.remember_choice = false;
    if (Array.isArray(alternates_shown) && alternates_shown.length > 0) body.alternates_shown = alternates_shown;
    if (confidence_score != null && !Number.isNaN(Number(confidence_score))) body.confidence_score = Number(confidence_score);
    if (clarify_rounds != null && !Number.isNaN(Number(clarify_rounds))) body.clarify_rounds = Number(clarify_rounds);
    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/factor-suggest-feedback', JSON.stringify(body), {
          Organisation: organisationId,
        }),
      );
      if (response?.data?.success) {
        const cached = response.data.cached !== false;
        dispatch(toggleAlertMessage(true, cached ? 'Saved for quicker matching next time.' : 'Choice recorded.', 'success', 3500));
        return { success: true, cached: response.data.cached, cache_key: response.data.cache_key };
      }
      return { error: true };
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.detail || data?.message || 'Could not save suggestion feedback.';
      dispatch(toggleAlertMessage(true, msg, 'error', 5000));
      return { error: true };
    }
  };
};

/**
 * Clears cached factor suggestions for the organisation.
 * @param {object} params - one of { cache_key }, { text }, or { clear_all: true }
 */
const clearFactorSuggestCache = (params = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const organisationId = getUsageModalOrganisationId(state);
    const body = {};
    if (params.cache_key) body.cache_key = params.cache_key;
    else if (params.text) body.text = String(params.text).trim();
    else if (params.clear_all) body.clear_all = true;
    else return { error: true };
    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/factor-suggest-cache-clear', JSON.stringify(body), {
          Organisation: organisationId,
        }),
      );
      if (response?.data?.success) {
        dispatch(toggleAlertMessage(true, `Cleared ${response.data.deleted || 0} cached suggestion(s).`, 'success', 3500));
        return { success: true, deleted: response.data.deleted };
      }
      return { error: true };
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.detail || data?.message || 'Could not clear cached suggestions.';
      dispatch(toggleAlertMessage(true, msg, 'error', 5000));
      return { error: true };
    }
  };
};

/**
 * Picks the quantity for the usage metric field from a factor-suggest response (API contract).
 * Prefers `usage_in_factor_units`, then `unit_conversion.converted_amount` (from `usage.amount * factor`
 * server-side), then `usage.amount` when `usage_compat.compatible` is not false.
 * Top-level `unit_conversion` duplicates copy for quick UI; the same rescale is mirrored under
 * `usage_derivation.conversions` as `rule_id: unit_normalization` for audit (alongside paper, flight, packaging).
 *
 * @param {object|null|undefined} snap - Full `/my/factor-suggest` response body
 * @returns {{ amount: number|null, skippedIncompatible: boolean, skipReason?: string, source?: string }}
 */
const resolveSuggestUsageForMetricField = (snap) => {
  const parseNonNegative = (v) => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isNaN(n) || n < 0) return null;
    return n;
  };

  if (!snap || typeof snap !== 'object') {
    return { amount: null, skippedIncompatible: false };
  }

  const fromUifu = parseNonNegative(snap.usage_in_factor_units?.amount);
  if (fromUifu != null) {
    return { amount: fromUifu, skippedIncompatible: false, source: 'usage_in_factor_units' };
  }

  const fromConverted = parseNonNegative(snap.unit_conversion?.converted_amount);
  if (fromConverted != null) {
    return { amount: fromConverted, skippedIncompatible: false, source: 'unit_conversion' };
  }

  const compat = snap.usage_compat;
  if (compat && compat.compatible === false) {
    return {
      amount: null,
      skippedIncompatible: true,
      skipReason:
        typeof compat.reason === 'string' && compat.reason.trim()
          ? compat.reason.trim()
          : 'Detected units do not match the factor without a conversion — enter usage in the factor’s units.',
    };
  }

  const fromUsage = parseNonNegative(snap.usage?.amount);
  if (fromUsage != null) {
    return { amount: fromUsage, skippedIncompatible: false, source: 'usage' };
  }

  return { amount: null, skippedIncompatible: false };
};

/**
 * Applies a factor-suggest candidate (best or alternate) via the same Redux paths as manual entry.
 * @param {object} candidate - API best / alternate object (subentity, metric, factor_dimensions, …)
 * @param {object} opts
 * @param {string} opts.dateEnd - ISO date string for factor filtering (same as modal item.dateEnd)
 * @param {object|null} [opts.suggestResponse] - Full factor-suggest response (preferred for usage pre-fill)
 * @param {number|string|null} [opts.usageAmount] - Legacy: raw amount when `suggestResponse` is omitted
 * @returns {Promise<{ success?: boolean, usageAmount?: number|null, usagePrefillSkipped?: boolean, usagePrefillReason?: string, error?: string }>}
 */
const applyFactorSuggestionToModal = (candidate, { dateEnd, suggestResponse, usageAmount } = {}) => {
  return async (dispatch, getState) => {
    if (!candidate?.subentity) {
      dispatch(toggleAlertMessage(true, 'Suggestion is missing a usage sub-type.', 'error'));
      return { error: 'INVALID_CANDIDATE' };
    }
    if (!dateEnd) {
      dispatch(toggleAlertMessage(true, 'End date is required to apply this suggestion.', 'error'));
      return { error: 'MISSING_DATE' };
    }

    const subentityId = String(candidate.subentity);
    const state0 = getState();
    const subRow = _.find(state0.subentities, (s) => String(s.id) === subentityId);

    if (!subRow) {
      dispatch(
        toggleAlertMessage(
          true,
          "This suggestion isn't available for your organisation's usage types.",
          'error',
        ),
      );
      return { error: 'INVALID_SUBENTITY' };
    }

    if (subRow.hideInUI) {
      dispatch(
        toggleAlertMessage(
          true,
          "This suggestion isn't available for your organisation's usage types.",
          'error',
        ),
      );
      return { error: 'HIDDEN_SUBENTITY' };
    }

    const entityList = Array.isArray(subRow.entity) ? subRow.entity : subRow.entity != null ? [subRow.entity] : [];
    const entityId =
      entityList.find((eid) => _.some(state0.entities, (ent) => String(ent.id) === String(eid))) ?? entityList[0];

    if (!entityId) {
      dispatch(toggleAlertMessage(true, 'Could not resolve a usage type for this suggestion.', 'error'));
      return { error: 'NO_ENTITY' };
    }

    dispatch(editUsageModal(entityId, { name: 'entity' }));
    await dispatch(getUsageOptions(entityId));

    const subOpt = subentityRowToSelectOption(subRow);
    dispatch(editUsageModal(subOpt, { name: 'subentity' }));

    let state = getState();
    const metricFromCandidate = candidate.metric != null ? String(candidate.metric) : null;
    const allowedMetrics = (Array.isArray(subRow.metric) ? subRow.metric : []).map(String);
    if (metricFromCandidate && allowedMetrics.length && !allowedMetrics.includes(metricFromCandidate)) {
      dispatch(toggleAlertMessage(true, 'Suggested metric is not valid for this usage sub-type.', 'error'));
      return { error: 'INVALID_METRIC' };
    }
    if (metricFromCandidate && String(state.usageDataModal.item.metric) !== metricFromCandidate) {
      dispatch(editUsageModal(metricFromCandidate, { name: 'metric' }));
      state = getState();
    }

    const item = state.usageDataModal.item;
    const metricId = item.metric;
    if (!metricId) {
      dispatch(toggleAlertMessage(true, 'Could not set a metric for this usage sub-type.', 'error'));
      return { error: 'MISSING_METRIC' };
    }

    await dispatch(getFilteredFactors(dateEnd, subentityId, !!subOpt.fuel_mix, metricId));

    const dims = candidate.factor_dimensions && typeof candidate.factor_dimensions === 'object' ? candidate.factor_dimensions : {};
    const usageOptKeys = Object.keys(getState().usageOptions || {});

    for (const key of usageOptKeys) {
      const rawVal = dims[key];
      if (rawVal == null || rawVal === '') continue;
      const stateSnap = getState();
      const list = stateSnap.usageOptions[key];
      const found = list?.some((x) => String(x.id) === String(rawVal));
      if (found) {
        dispatch(editUsageModal(rawVal, { name: key }));
      }
    }

    let appliedUsage = null;
    let usagePrefillSkipped = false;
    let usagePrefillReason;

    if (suggestResponse != null && typeof suggestResponse === 'object') {
      const resolved = resolveSuggestUsageForMetricField(suggestResponse);
      if (resolved.skippedIncompatible) {
        usagePrefillSkipped = true;
        usagePrefillReason = resolved.skipReason;
        dispatch(
          toggleAlertMessage(
            true,
            usagePrefillReason,
            'warning',
            7000,
          ),
        );
      } else if (resolved.amount != null) {
        appliedUsage = resolved.amount;
        dispatch(editUsageModal(String(resolved.amount), { name: 'usage' }));
      }
    } else if (usageAmount != null && usageAmount !== '') {
      const n = typeof usageAmount === 'number' ? usageAmount : parseFloat(String(usageAmount).replace(/,/g, ''));
      if (!Number.isNaN(n) && n >= 0) {
        appliedUsage = n;
        dispatch(editUsageModal(String(n), { name: 'usage' }));
      }
    }

    return {
      success: true,
      usageAmount: appliedUsage,
      usagePrefillSkipped,
      usagePrefillReason,
    };
  };
};

const showUploadUsageDataModal = (status) => ({
  type: Types.USAGE_IMPORT_SHOW_MODAL,
  status,
});

const showUploadHistory = (status) => ({
  type: Types.USAGE_IMPORT_SHOW_HISTORY,
  status,
});

const updateUsageOptions = (payload) => ({
  type: Types.USAGE_OPTIONS,
  payload,
});

const updateUsageCacheOptions = (payload) => ({ type: Types.USAGE_CACHE_OPTIONS, payload });

const getUsageOptions = (entity) => {
  return async (dispatch, getState) => {
    const state = getState();

    if (state?.cache?.currentEntity === entity) return;

    dispatch(showLoader(true));

    try {
      const version = getAppVersion();
      const addOns = state?.organisation?.details?.addOns;
      const postfix = version === 'v1' ? '' : `-${versionToPostfix(version)}`;
      const archer = window.location.hostname.includes('archer');
      const link = archer ? 'archerim.downloads.compareyourfootprint.com' : 'downloads.compareyourfootprint.com';
      const conversionFactors = _.groupBy(
        _.unionBy(
          ...(await Promise.all(
            addOns?.map((addOnId) =>
              axios(`https://s3.eu-west-2.amazonaws.com/${link}/conversionFactors${postfix}/${addOnId}/${entity}.json`)
                .then((res) => res.data)
                .catch((err) => {
                  console.log('getUsageOptions conversionFactors Error', addOnId, err);
                  return [];
                }),
            ),
          )),
          'id',
        ),
        'subentity',
      );

      const marketFactors = _.groupBy(
        _.unionBy(
          ...(await Promise.all(
            addOns?.map((addOnId) =>
              axios(`https://s3.eu-west-2.amazonaws.com/${link}/marketFactors${postfix}/${addOnId}/${entity}.json`)
                .then((res) => res.data)
                .catch((err) => {
                  console.log('getUsageOptions marketFactors Error', addOnId, err);
                  return [];
                }),
            ),
          )),
          'id',
        ),
        'subentity',
      );

      const currentEntity = entity;

      console.log('getUsageOptions', { conversionFactors, marketFactors });
      dispatch(updateUsageCacheOptions({ conversionFactors, marketFactors, currentEntity }));
      dispatch(showLoader(false));
    } catch (e) {
      console.error('getUsageOptions Error:', e);
    }
  };
};

const getTemplates = () => {
  return async (dispatch, getState) => {
    dispatch(showLoader(true));

    try {
      const response = await axios(await createSignedRequest('GET', apiURL + `/my/templates`));

      dispatch(setTemplateOptions(response.data));
      dispatch(showLoader(false));
      return response.data;
    } catch (error) {
      dispatch(showLoader(false));
      console.error('Error:', error);
    }
  };
};

const resetUsageDataModal = (item) => ({
  type: Types.USAGE_RESET_MODAL,
  item,
});

const getUsageDataAuditReport = () => {
  return async (dispatch, getState) => {
    const state = getState();

    dispatch(updateDownloadStatus(true, 'auditReport'));

    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          apiURL + `/my/usage/auditReport?orgId=${encodeURIComponent(state.currentOrganisation)}`,
          null,
          {
            Organisation: String(state.currentOrganisation),
          },
        ),
      );

      console.log('getUsageDataAuditReport', response);
      dispatch(getStepFunctionStatus(response.data.data.executionArn));
    } catch (e) {
      console.log('getUsageDataAuditReport Error:', e);
      dispatch(toggleAlertMessage(true, 'There was an error generating your usage data audit report!', 'error'));
      dispatch(updateDownloadStatus(false, 'auditReport'));
    }
  };
};

const Actions = {
  getTemplates,
  saveUsageData,
  getUsageTotals,
  toggleProcessingUsage,
  showUsageDataModal,
  showUsageDataDeleteModal,
  showAnalysisImportModal,
  deleteUsageData,
  uploadUsageType,
  processUpload,
  confirmUploadEstimate,
  showUploadUsageDataModal,
  showUploadHistory,
  getUploadLink,
  getMyUploads,
  getFilteredFactors,
  editUsageModal,
  getUsageById,
  updateUsageData,
  updateUsageTotals,
  getUsageOptions,
  resetUsageDataModal,
  getUsageDataAuditReport,
  suggestFactorFromText,
  applyFactorSuggestionToModal,
  submitFactorSuggestFeedback,
  clearFactorSuggestCache,
};

export { Actions, Types };
